import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { API } from "@/App";
import { Lock, User, Shield } from "lucide-react";

const Login = () => {
  const [adminCreds, setAdminCreds] = useState({ username: "", password: "" });
  const [userCreds, setUserCreds] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, adminCreds);
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("userType", "admin");
      toast.success("Erfolgreich angemeldet!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const handleUserLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/user/login`, userCreds);
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("userType", "user");
      toast.success("Erfolgreich angemeldet!");
      navigate("/user/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#F5F5F5' }}>
      <Card className="w-full max-w-md card-white animate-slide-in" data-testid="login-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-xl overflow-hidden shadow-lg" style={{ background: 'linear-gradient(135deg, #DC4444 0%, #B91C1C 100%)' }}>
              <img src="/logo.png" alt="Logo" className="w-full h-full object-contain p-2" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">Payment Management</CardTitle>
          <CardDescription className="text-gray-600 font-medium">Feuerwehr Kantine</CardDescription>
          <CardDescription className="text-gray-500 text-sm mt-1">Melden Sie sich an, um fortzufahren</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="user" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="admin" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Admin
              </TabsTrigger>
              <TabsTrigger value="user" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Wachabteilung
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="admin">
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-username" className="text-gray-700">Benutzername</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="admin-username"
                      data-testid="admin-username-input"
                      type="text"
                      value={adminCreds.username}
                      onChange={(e) => setAdminCreds({ ...adminCreds, username: e.target.value })}
                      required
                      className="pl-10 bg-white border-gray-300 text-gray-900"
                      placeholder="admin"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-password" className="text-gray-700">Passwort</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="admin-password"
                      data-testid="admin-password-input"
                      type="password"
                      value={adminCreds.password}
                      onChange={(e) => setAdminCreds({ ...adminCreds, password: e.target.value })}
                      required
                      className="pl-10 bg-white border-gray-300 text-gray-900"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="admin-login-button"
                  className="w-full btn-primary text-white font-semibold py-2.5"
                  disabled={loading}
                >
                  {loading ? "Wird angemeldet..." : "Anmelden"}
                </Button>
                <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-xs text-blue-800 text-center">
                    Hier nicht als Admin deiner Wachabteilung
                  </p>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="user">
              <form onSubmit={handleUserLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="user-username" className="text-gray-700">Benutzername</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="user-username"
                      data-testid="user-username-input"
                      type="text"
                      value={userCreds.username}
                      onChange={(e) => setUserCreds({ ...userCreds, username: e.target.value })}
                      required
                      className="pl-10 bg-white border-gray-300 text-gray-900"
                      placeholder="Ihr Benutzername"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-password" className="text-gray-700">Passwort</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="user-password"
                      data-testid="user-password-input"
                      type="password"
                      value={userCreds.password}
                      onChange={(e) => setUserCreds({ ...userCreds, password: e.target.value })}
                      required
                      className="pl-10 bg-white border-gray-300 text-gray-900"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="user-login-button"
                  className="w-full btn-primary text-white font-semibold py-2.5"
                  disabled={loading}
                >
                  {loading ? "Wird angemeldet..." : "Anmelden"}
                </Button>
                <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-xs text-green-800 text-center">
                    Hier loggst du dich als Admin deiner Wachabteilung ein.
                  </p>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;